import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { FiClock } from "react-icons/fi";
import { showError } from "../../../utils/toastUtils";
import { convertTo12Hour } from "../../../utils/formatDateTime";
import { MapPin } from "lucide-react";

const getTodayISO = () => new Date().toISOString().split("T")[0];

const addDays = (dateStr, days) => {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().split("T")[0];
};

const isFutureSlot = (dateISO, startTime) => {
  const now = new Date();
  const [h, m] = startTime.split(":").map(Number);

  const slotDate = new Date(dateISO);
  slotDate.setHours(h, m, 0, 0);

  return slotDate > now;
};

const UserSlotPicker = ({
  value,
  onChange,
  isPage,
  latitude,
  longitude,
  branchId,
}) => {
  const todayISO = getTodayISO();
  const hasValidCoords =
    typeof latitude === "number" && typeof longitude === "number";

  const [selectedDate, setSelectedDate] = useState(todayISO);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);

  const visibleDates = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(todayISO, i)),
    [todayISO],
  );

  const fetchSlots = async (date) => {
    if (!branchId && !hasValidCoords) return;
    setLoading(true);
    setSlots([]);

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/slot-availability/available`,
        {
          params: {
            date,
            ...(branchId ? { branchId } : { latitude, longitude }),
          },
        },
      );
      
      // console.log("data", res.data.data);
      let data = res.data?.data || [];

      if (date === todayISO) {
        data = data.filter((s) => isFutureSlot(date, s.startTime));
      }

      setSlots(data);
    } catch (err) {
      console.error(err);
      showError("Failed to load slots");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSlots(selectedDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, branchId, latitude, longitude]);

  const isSlotClickable = (slot) => {
    if (slot.isBooked) return false;
    if (slot.isDisabled) return false;
    if (slot.isPast) return false;
    return true;
  };

  return (
    <div className="space-y-4 ">
      {/* DATE STRIP */}
      <div
        className={`flex gap-2 overflow-x-auto no-scrollbar pb-1 w-full min-w-0  ${
          isPage ? "max-w-full" : "max-w-[23.4rem] "
        } `}
      >
        {visibleDates.map((date) => {
          const isActive = date === selectedDate;
          const d = new Date(date);

          return (
            <button
              key={date}
              onClick={() => {
                setSelectedDate(date);
                onChange(null);
              }}
              className={`flex-shrink-0 w-12 p-1.5 rounded-xl border text-xs font-semibold transition-all
                ${
                  isActive
                    ? "bg-primaryColor/90 text-white border-primaryColor"
                    : "bg-white border-gray-200 text-gray-700"
                }
              `}
            >
              <div className="text-sm font-bold">{d.getDate()}</div>
              <div className="text-[0.65rem] opacity-80">
                {d.toLocaleDateString("en-US", { weekday: "short" })}
              </div>
            </button>
          );
        })}
      </div>
      {/* NEAREST BRANCH INFO */}
      <div className="flex items-center justify-center gap-1 text-[0.7rem] text-gray-500">
        <MapPin size={12} className="opacity-70" />
        <span>Serving from nearest branch</span>
      </div>

      <div className="py-3">
        {/* SLOTS */}
        {loading ? (
          <SlotSkeleton />
        ) : slots.length === 0 ? (
          <EmptySlots />
        ) : (
          <div className="grid grid-cols-4 gap-2 w-full min-w-0 ">
            {slots.map((slot) => {
              const isSelected = value?.availabilityId === slot.availabilityId;

              return (
                <button
                  key={slot.availabilityId}
                  onClick={() => {
                    if (!isSlotClickable(slot)) return;

                    onChange({
                      ...slot,
                      date: selectedDate,
                    });
                  }}
                  className={`flex flex-col border text-gray-800 p-2  rounded-xl text-xs font-semibold  items-center justify-center gap-1 transition
                    ${
                      isSelected
                        ? "bg-primaryColor/90 text-white border-primaryColor"
                        : "border border-accentTextColor"
                    }`}
                >
                  <div className="  ">{convertTo12Hour(slot.startTime)}</div>
                  <div className="text-[0.67rem] w-full text-center ">to</div>
                  <div className="">{convertTo12Hour(slot.endTime)}</div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const SlotSkeleton = () => {
  return (
    <div className="grid grid-cols-4 gap-2">
      {Array.from({ length: 8 }).map((_, i) => (
        <div
          key={i}
          className="p-3 rounded-lg border border-gray-200 animate-pulse flex flex-col items-center justify-center gap-2"
        >
          <div className="h-4 w-12 bg-gray-200 rounded" />
          <div className="h-2 w-6 bg-gray-200 rounded" />
          <div className="h-3 w-12 bg-gray-200 rounded" />
        </div>
      ))}
    </div>
  );
};

const EmptySlots = () => {
  return (
    <div className="py-8 px-4 text-center border border-dashed rounded-xl text-gray-500 text-sm">
      <FiClock className="mx-auto mb-2 text-lg opacity-60" />
      <p className="font-semibold">No slots available</p>
      <p className="text-xs mt-1 opacity-70">Please try another date</p>
    </div>
  );
};

export default UserSlotPicker;
